import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router";

import { validate } from "capitalrx-components-2.0";
import { Dropdown, Form, Textbox } from "capitalrx-components-2.0/lib/forms";
import { LoadingGroup, LoadingSkeleton, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Subheading, Subtitle } from "capitalrx-components-2.0/lib/typography";
import styled from "styled-components";

import { DrugConfiguration, useGetDrugQuery } from "@/App/api/drug";
import { locationGeolocate } from "@/App/api/pharmacy";
import { ClientAwareLink, ContentPanel, PageWrapper, ThemedButton, ThemedCheckbox, ThemedSubmitButton } from "@/App/components";
import { AuthenticationContext } from "@/App/context";
import { useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { inflectQuantity } from "@/App/utils/formatSelectorDrugQuantity";

type InitPhase = "loading" | "initializing" | "ready";

const GenericAlternativeSuggestion = styled.div`
  flex-grow: 1;
  a {
    font-size: inherit !important;
  }
  background-color: #f3f7f4;
  padding: 6px;
  border-radius: 8px;
`;

interface FindBestPriceDrugDetailForm {
  readonly alternativeDrug: number;
  readonly drugForm: string;
  readonly dosage: string;
  readonly quantity: string;
  readonly daysSupply: string;
  readonly useCustomDaysSupply: boolean;
  readonly useCurrentLocation: boolean;
  readonly useCustomQuantity: boolean;
  readonly location: string;
}

const ZIP_REGEX = new RegExp("d{5}");

export const FindBestPriceDrugDetailPage: FC = () => {
  const { t: translate } = useTranslation();
  const { drug_id: drugId } = useParams();
  const [searchParams] = useSearchParams();
  const { data: drugData, isLoading: isLoadingDrug } = useGetDrugQuery(+drugId);
  const { getAuthToken } = useContext(AuthenticationContext);
  const [currentLocation, setCurrentLocation] = useState({});
  const navigate = useNavigateClient();
  const alert = useAlert();

  const urlDosageForm = searchParams.get("dosage_form");
  const urlDosage = searchParams.get("dosage");
  const urlQuantity = searchParams.get("quantity");
  const urlDaysSupply = searchParams.get("days_supply");
  const urlLocation = searchParams.get("loc");
  const urlLocationText = searchParams.get("location_text");

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });

  const drug = drugData?.drug;
  const alternatives = drugData?.alternatives;

  const onBackPressed = useCallback(() => {
    navigate({
      pathname: "/find-best-price",
      search: searchParams.toString(),
    });
  }, [searchParams]);

  const drugConfigForm = useForm<FindBestPriceDrugDetailForm>();
  const drugForm = drugConfigForm.watch("drugForm");
  const drugDosage = drugConfigForm.watch("dosage");
  const useCustomDaysSupply = drugConfigForm.watch("useCustomDaysSupply");
  const useCurrentLocation = drugConfigForm.watch("useCurrentLocation");
  const useCustomQuantity = drugConfigForm.watch("useCustomQuantity");
  const alternativeDrug = drugConfigForm.watch("alternativeDrug");

  const [initPhase, setInitPhase] = useState<InitPhase>("loading");
  const urlParamsKey = `${urlDosageForm}|${urlDosage}|${urlQuantity}|${urlDaysSupply}|${urlLocationText}`;
  const prevUrlParamsKeyRef = useRef<string | null>(null);
  const initializedValuesRef = useRef<Partial<FindBestPriceDrugDetailForm> | null>(null);

  useEffect(() => {
    const hasUrlParams = urlDosageForm && urlDosage && urlQuantity && urlDaysSupply;
    if (prevUrlParamsKeyRef.current !== null && prevUrlParamsKeyRef.current !== urlParamsKey && hasUrlParams) {
      setInitPhase("loading");
      initializedValuesRef.current = null;
    }
    prevUrlParamsKeyRef.current = urlParamsKey;
  }, [urlParamsKey, urlDosageForm, urlDosage, urlQuantity, urlDaysSupply]);

  useEffect(() => {
    if (initPhase !== "ready") return;
    if (initializedValuesRef.current?.drugForm === drugForm) return;
    drugConfigForm.setValue("dosage", drugDosageList[0]);
  }, [drugForm]);

  useEffect(() => {
    if (initPhase !== "ready") return;
    if (initializedValuesRef.current?.dosage === drugDosage) return;
    drugConfigForm.setValue("quantity", drugQuantitiesList[0]);
  }, [drugDosage, drugForm]);

  useEffect(() => {
    if (initPhase !== "ready") return;
    if (initializedValuesRef.current?.useCustomDaysSupply === useCustomDaysSupply) return;
    if (useCustomDaysSupply) {
      drugConfigForm.setValue("daysSupply", "");
    } else {
      drugConfigForm.setValue("daysSupply", "30");
    }
  }, [useCustomDaysSupply]);

  useEffect(() => {
    if (initPhase !== "ready") return;
    if (initializedValuesRef.current?.useCustomQuantity === useCustomQuantity) return;
    if (useCustomQuantity) {
      drugConfigForm.setValue("quantity", "");
    } else {
      drugConfigForm.setValue("quantity", drugQuantitiesList[0]);
    }
  }, [useCustomQuantity]);

  useEffect(() => {
    if (initPhase !== "ready") return;
    if (initializedValuesRef.current?.useCurrentLocation === useCurrentLocation) return;
    if (useCurrentLocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString(),
          });
          drugConfigForm.setValue("location", "");
        },
        () => {
          alert({
            message: translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.unableToGetCurrentLocation"),
            type: "warning",
            style: "toast",
          });
          drugConfigForm.setValue("useCurrentLocation", false);
        },
      );
    }
  }, [useCurrentLocation]);

  useEffect(() => {
    if (alternativeDrug && alternativeDrug !== +drugId) {
      navigate(`/find-best-price/${alternativeDrug}`);
    }
  }, [alternativeDrug]);

  const validateCustomNumeral = useCallback((value: string) => {
    const isNumeric = validate.isNumeric(value);
    if (isNumeric === true) {
      if (+value > 9999 || +value < 1) {
        return translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.valueMustBeBetween");
      }
    } else {
      return isNumeric;
    }
    return true;
  }, []);

  const drugFormList = useMemo(() => {
    if (drug) {
      const drugFormList = drug.configurations.reduce((accum: Array<string>, configuration: DrugConfiguration) => {
        if (!accum.includes(configuration.dosage_form)) {
          accum.push(configuration.dosage_form);
        }

        return accum;
      }, []);

      return drugFormList.sort();
    } else {
      return [];
    }
  }, [drug]);

  const drugDosageList = useMemo(() => {
    if (drug && drugForm) {
      const dosageList = drug.configurations.reduce((accum: Array<string>, configuration: DrugConfiguration) => {
        if (configuration.dosage_form === drugForm && !accum.includes(configuration.dosage)) {
          accum.push(configuration.dosage);
        }

        return accum;
      }, []);

      return dosageList.sort((currentDosage, nextDosage) => {
        return currentDosage.localeCompare(nextDosage, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
    } else {
      return [];
    }
  }, [drug, drugForm]);

  const drugQuantitiesList = useMemo(() => {
    if (drug) {
      let newDrugQuantityList: Array<string> = [];

      const drugConfiguration = drug.configurations.find((configuration) => {
        return configuration.dosage_form === drugForm && configuration.dosage === drugDosage;
      });

      if (drugConfiguration) {
        const dosageFormPackaging = drugConfiguration.dosage_form_packaging || drugConfiguration.dosage_form;
        newDrugQuantityList = drugConfiguration.display_quantities.map((display_quantity) => {
          const inflectDrugQuantity = inflectQuantity(display_quantity, dosageFormPackaging);
          return `${inflectDrugQuantity}`;
        });
      }

      return newDrugQuantityList;
    } else {
      return [];
    }
  }, [drug, drugForm, drugDosage]);

  // get packaging form for selected drug and drug form
  const getDrugDosagePackaging = useCallback(
    (drugForm: string): string => {
      const drugDosagePackaging = drug?.configurations?.find((options) => {
        if (options.dosage_form === drugForm) {
          return options;
        }
      });

      if (!drugDosagePackaging?.dosage_form_packaging) {
        return drugDosagePackaging?.dosage_form;
      }

      return drugDosagePackaging?.dosage_form_packaging;
    },
    [drug?.configurations],
  );

  // get default dosage because most_common_dosage isn't always the full text
  const defaultDosage = useCallback((): string => {
    const commonDosage = drug?.most_common_dosage;

    const defaultDosage = drug?.configurations?.find((options) => {
      if (options.dosage_form === drug?.most_common_form && options.dosage.includes(commonDosage)) {
        return options;
      }
    });

    return defaultDosage?.dosage;
  }, [drug?.configurations, drug?.most_common_dosage, drug?.most_common_form]);

  // calculate default displayed quantity
  const defaultDisplayQuantity = useCallback((): number => {
    if (drug?.most_common_quantity_dispensed) {
      const commonQuantity = parseInt(drug?.most_common_quantity_dispensed);

      const drugQuantitiesMultiplier = drug?.configurations?.find((options) => {
        if (options.dosage_form === drug?.most_common_form && options.dosage.includes(drug?.most_common_dosage)) {
          return options;
        }
      });

      return Math.round(commonQuantity / drugQuantitiesMultiplier?.quantity_multiplier);
    }

    return parseInt(drug?.configurations[0]?.display_quantities[0]);
  }, [drug?.most_common_quantity_dispensed, drug?.configurations, drug?.most_common_form, drug?.most_common_dosage]);

  useEffect(() => {
    if (initPhase !== "loading" || !drug) return;

    setInitPhase("initializing");

    let initialValues: FindBestPriceDrugDetailForm;

    if (urlDosageForm && urlDosage && urlQuantity && urlDaysSupply) {
      const drugConfig = drug.configurations.find((c) => c.dosage_form === urlDosageForm && c.dosage === urlDosage);
      const dosageFormPackaging = drugConfig?.dosage_form_packaging || urlDosageForm;
      const displayQuantities = (drugConfig?.display_quantities || []).map((dq) => inflectQuantity(String(dq), dosageFormPackaging));

      if (urlLocation) {
        try {
          setCurrentLocation(JSON.parse(decodeURIComponent(urlLocation)));
        } catch {
          /* invalid JSON, ignore */
        }
      }

      initialValues = {
        alternativeDrug: drug.id,
        drugForm: urlDosageForm,
        dosage: urlDosage,
        quantity: urlQuantity,
        daysSupply: urlDaysSupply,
        useCustomDaysSupply: urlDaysSupply !== "30" && urlDaysSupply !== "90",
        useCurrentLocation: false,
        useCustomQuantity: !displayQuantities.includes(urlQuantity),
        location: urlLocationText || "",
      };
    } else {
      let useCustomQuantity = false;
      const commonDosage = defaultDosage();
      const commonQuantity = defaultDisplayQuantity();

      let drugDefaultForm = drug?.most_common_form || drug?.configurations[0]?.dosage_form || "";
      let drugDefaultDosage = drug?.most_common_form ? commonDosage : drug?.configurations[0]?.dosage || "";

      let useQuantity: string;

      if (!drugDefaultDosage || !drugDefaultForm || !commonQuantity) {
        drugDefaultDosage = drugDosageList[0];
        drugDefaultForm = drugFormList[0];
        useQuantity = drugQuantitiesList[0];
      } else {
        const dosagePackagingVariable = getDrugDosagePackaging(drugDefaultForm);
        useQuantity = inflectQuantity("" + commonQuantity, dosagePackagingVariable);

        const configForForm = drug.configurations.find((c) => c.dosage_form === drugDefaultForm && c.dosage === drugDefaultDosage);
        const displayQtys = (configForForm?.display_quantities || []).map((dq) => inflectQuantity(String(dq), dosagePackagingVariable));
        if (!displayQtys.includes(useQuantity)) {
          useCustomQuantity = true;
          useQuantity = "" + commonQuantity;
        }
      }

      initialValues = {
        alternativeDrug: drug.id,
        drugForm: drugDefaultForm,
        dosage: drugDefaultDosage,
        quantity: useQuantity || "",
        daysSupply: "30",
        useCustomDaysSupply: false,
        useCurrentLocation: false,
        useCustomQuantity: useCustomQuantity,
        location: "",
      };
    }

    initializedValuesRef.current = initialValues;
    drugConfigForm.reset(initialValues);
    setTimeout(() => setInitPhase("ready"), 0);
  }, [
    drug,
    initPhase,
    urlDosageForm,
    urlDosage,
    urlQuantity,
    urlDaysSupply,
    urlLocation,
    urlLocationText,
    drugFormList,
    drugDosageList,
    drugQuantitiesList,
    defaultDosage,
    defaultDisplayQuantity,
    getDrugDosagePackaging,
  ]);

  const onSubmit = useCallback(
    async (formData: FindBestPriceDrugDetailForm) => {
      const searchParams = new URLSearchParams();
      searchParams.set("dosage", formData.dosage);
      searchParams.set("dosage_form", formData.drugForm);
      searchParams.set("quantity", formData.quantity);
      searchParams.set("days_supply", formData.daysSupply);

      // get the user's location
      if (!formData.useCurrentLocation) {
        let geolocateBody = {};
        const location = formData.location;
        if (ZIP_REGEX.test(location)) {
          geolocateBody = { zip_code: location };
        } else {
          geolocateBody = { search_address: location };
        }
        try {
          const { latitude, longitude } = await locationGeolocate(await getAuthToken(), geolocateBody);
          searchParams.set("loc", encodeURIComponent(JSON.stringify({ latitude, longitude })));
          searchParams.set("location_text", formData.location);
        } catch {
          alert({
            style: "toast",
            type: "error",
            message: translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.unableToFindLocation"),
          });
          return;
        }
      } else {
        searchParams.set("loc", encodeURIComponent(JSON.stringify(currentLocation)));
      }

      logEvent(ANALYTIC_EVENTS.PRESCRIPTION.findLowestPriceButton);

      const configParams = new URLSearchParams(window.location.search);
      configParams.set("dosage_form", formData.drugForm);
      configParams.set("dosage", formData.dosage);
      configParams.set("quantity", formData.quantity);
      configParams.set("days_supply", formData.daysSupply);
      configParams.set("loc", searchParams.get("loc"));
      configParams.set("location_text", formData.location);
      window.history.replaceState(null, "", `${window.location.pathname}?${configParams.toString()}`);

      navigate({
        pathname: `/find-best-price/compare-prices/${drug.id}`,
        search: searchParams.toString(),
      });
    },
    [drug, currentLocation],
  );

  const genericAlternative = useMemo(() => {
    if (alternatives && drug?.drug_type === "Brand") {
      return alternatives.find((alt) => alt.drug.drug_type === "Generic");
    }
    return undefined;
  }, [alternatives]);

  return (
    <PageWrapper>
      <LoadingGroup loading={isLoadingDrug}>
        <ContentPanel
          title={
            <Shelf>
              <ThemedButton testId="go-back" onClick={onBackPressed} appearance="secondary" icon="arrow-left">
                {translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.back")}
              </ThemedButton>
              <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 200, height: 48 }} />}>
                <Subtitle testId="drug-name">{drug?.drug_name}</Subtitle>
              </LoadingGroup.Placeholder>
              <Shelf.Spacer />
            </Shelf>
          }
        >
          <Form form={drugConfigForm} onSubmit={onSubmit} testId="drug-config">
            <Grid>
              {!isCompressed && <Grid.Span columns={2} />}
              <Grid.Span columns={isCompressed ? 12 : 8}>
                <Stack spacing="medium">
                  <Dropdown.Field
                    testId="alternatives-selection"
                    required
                    label={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.type")}
                    name="alternativeDrug"
                    readOnly={(alternatives || []).length === 0}
                  >
                    {drug?.drug_type === "Brand" && (
                      <Dropdown.Option value={drug?.id}>{translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.brand")}</Dropdown.Option>
                    )}
                    {drug?.drug_type === "Generic" && (
                      <Dropdown.Option value={drug?.id}>{translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.generic")}</Dropdown.Option>
                    )}
                    {(alternatives || []).map((alternative) => {
                      return (
                        <Dropdown.Option key={alternative.drug.id} value={alternative.drug.id}>
                          {alternative.drug.drug_name} {`(${alternative.drug.drug_type})`}
                        </Dropdown.Option>
                      );
                    })}
                  </Dropdown.Field>

                  {!!genericAlternative && (
                    <GenericAlternativeSuggestion data-testid="div-generic-alternative-suggestion">
                      <Subheading textAlign="center">
                        {translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.tryThisGenericAlternative")}{" "}
                        <ClientAwareLink to={`/find-best-price/${genericAlternative.drug.id}`} testId="go-to-generic-drug">
                          {genericAlternative.drug.drug_name}
                        </ClientAwareLink>
                      </Subheading>
                    </GenericAlternativeSuggestion>
                  )}

                  <Dropdown.Field
                    name="drugForm"
                    label={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.form")}
                    required
                    placeholder={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.form")}
                    testId="drug-form-selection"
                  >
                    {drugFormList.map((form) => {
                      return (
                        <Dropdown.Option key={form} value={form}>
                          {form}
                        </Dropdown.Option>
                      );
                    })}
                  </Dropdown.Field>

                  <Dropdown.Field
                    name="dosage"
                    label={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.dosage")}
                    required
                    placeholder={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.dosage")}
                    testId="drug-dosage-selection"
                  >
                    {drugDosageList.map((dosage) => {
                      return (
                        <Dropdown.Option key={dosage} value={dosage}>
                          {dosage}
                        </Dropdown.Option>
                      );
                    })}
                  </Dropdown.Field>

                  <Stack spacing="small">
                    {useCustomQuantity && (
                      <Textbox.Field
                        testId="custom-quantity"
                        style={{ flexGrow: 1 }}
                        name="quantity"
                        label={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.quantity")}
                        required
                        validator={validateCustomNumeral}
                        placeholder={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.enterNumberBetween")}
                      />
                    )}
                    {!useCustomQuantity && (
                      <Dropdown.Field
                        style={{ flexGrow: 1 }}
                        name="quantity"
                        label={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.quantity")}
                        required
                        placeholder={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.quantity")}
                        testId="preset-quantity-selection"
                      >
                        {drugQuantitiesList.map((quantity) => {
                          return (
                            <Dropdown.Option key={quantity} value={quantity}>
                              {quantity}
                            </Dropdown.Option>
                          );
                        })}
                      </Dropdown.Field>
                    )}
                    <ThemedCheckbox.Field
                      testId="quantity-entry-type-selection"
                      hideLabel
                      name="useCustomQuantity"
                      label={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.enterCustomQuantity")}
                    />
                  </Stack>

                  <Stack spacing="small">
                    {useCustomDaysSupply && (
                      <Textbox.Field
                        testId="custom-days-supply"
                        style={{ flexGrow: 1 }}
                        name="daysSupply"
                        label={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.daysSupply")}
                        required
                        validator={validateCustomNumeral}
                        placeholder={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.enterNumberBetween")}
                      />
                    )}
                    {!useCustomDaysSupply && (
                      <Dropdown.Field
                        style={{ flexGrow: 1 }}
                        name="daysSupply"
                        label={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.daysSupply")}
                        required
                        placeholder={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.daysSupply")}
                        testId="preset-days-supply-selection"
                      >
                        <Dropdown.Option value="30">30</Dropdown.Option>
                        <Dropdown.Option value="90">90</Dropdown.Option>
                      </Dropdown.Field>
                    )}
                    <ThemedCheckbox.Field
                      testId="days-supply-entry-type-selection"
                      hideLabel
                      name="useCustomDaysSupply"
                      label={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.enterCustomDaysSupply")}
                    />
                  </Stack>

                  {/* <ThemedRadioGroup.Field
                    label="Days Supply"
                    name="daysSupplyType"
                    orientation="horizontal"
                    testId="days-supply-entry-type-selection"
                  >
                    <ThemedRadioGroup.Option value="30">30</ThemedRadioGroup.Option>
                    <ThemedRadioGroup.Option value="90">90</ThemedRadioGroup.Option>
                    <ThemedRadioGroup.Option value="custom">Custom</ThemedRadioGroup.Option>
                  </ThemedRadioGroup.Field> */}

                  {/* {daysSupplyType === "custom" && (
                    <Textbox.Field
                      testId="custom-days-supply"
                      required
                      placeholder="Enter a value between 1 - 9999"
                      name="daysSupply"
                      validator={validateCustomNumeral}
                      hideLabel
                    />
                  )} */}

                  <Stack spacing="small">
                    <Stack style={{ flexGrow: 1 }} spacing="mini">
                      <Textbox.Field
                        testId="location"
                        required
                        disabled={useCurrentLocation}
                        label={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.location")}
                        placeholder={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.enterCityOrZipCode")}
                        name="location"
                        style={{ flexGrow: 1 }}
                      />

                      <Shelf>
                        <ThemedCheckbox.Field
                          testId="location-entry-type-selection"
                          hideLabel
                          name="useCurrentLocation"
                          label={translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.useMyCurrentLocation")}
                        />
                        <Shelf.Spacer />
                      </Shelf>
                    </Stack>
                  </Stack>

                  <Shelf alignment="center">
                    <ThemedSubmitButton icon="arrow-right" iconPosition="right" testId="find-best-price-submit">
                      {translate("FIND_BEST_PRICE.findBestPriceDrugConfigPage.findBestPrice")}
                    </ThemedSubmitButton>
                  </Shelf>
                </Stack>
              </Grid.Span>
              {!isCompressed && <Grid.Span columns={2} />}
            </Grid>
          </Form>
        </ContentPanel>
      </LoadingGroup>
    </PageWrapper>
  );
};
